import { useEffect, useRef } from "react";
import { useDispatch, useStore } from "react-redux";
import { setProgress, resetProgress, setNeedsRefetch } from "@/redux/reducers/InterviewReducer";
import { CandidateService } from "@/services/candidateService";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";
import { FALLBACK_TYPES } from "../interview/constants/interviewTypes";

const POLL_INTERVAL_MS = 5000;

const formatLabel = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Tab-independent interview-generation lock.
 *
 * The Interview tab's own hook only detects in-flight generation while that tab
 * is mounted. On a mid-generation refresh the user lands on the default tab, so
 * the lock is lost and the status button re-enables. This hook lives in the
 * parent (CandidateAnalysis) and recovers the lock from the backend (the source
 * of truth — generation status is persisted) on any tab, then polls until the
 * generation finishes and clears the lock + triggers a refetch.
 *
 * It writes to the same `interview` Redux slot the live stream uses, so the
 * status button (driven by Redux `regenProgress`) stays disabled across refresh.
 *
 * **It does nothing at all without the module.** This hook runs on the parent,
 * so it ran for every candidate-analysis page of every tenant, and its first
 * call — `GET /ai-tools/interview-question-types/active` — carries an
 * entitlement gate. A tenant without `ai.interview_questions` therefore met one
 * silent 403 on every candidate they opened. There is nothing for the hook to
 * recover either way: a tenant who cannot generate has no generation to lock.
 *
 * @param {string} applicationId
 * @param {string} jobId
 * @param {() => void} onComplete - called once when a recovered generation finishes
 */
const useGenerationLock = (applicationId, jobId, onComplete) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { isEnabled: moduleEnabled } = useModuleAccess(MODULES.AI_INTERVIEW_QUESTIONS);

  // Types this hook marked as processing — used to detect the processing→done
  // transition so we only clear/refetch for locks we own (never clobber the
  // Interview tab's live stream, which manages its own slot).
  const lockedRef = useRef(new Set());
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!applicationId || !moduleEnabled) return;

    let cancelled = false;
    let timer = null;
    const locked = lockedRef.current;

    const resolveTypes = async () => {
      if (!jobId) return FALLBACK_TYPES;
      try {
        const types = await CandidateService.fetchJobInterviewQuestionTypes(jobId);
        return Array.isArray(types) && types.length > 0 ? types : FALLBACK_TYPES;
      } catch {
        return FALLBACK_TYPES;
      }
    };

    const poll = async (types) => {
      if (cancelled) return;

      const results = await Promise.all(
        types.map((t) =>
          CandidateService.getInterviewStatus(applicationId, t.value)
            .then((res) => ({ type: t.value, status: res?.status }))
            .catch(() => ({ type: t.value, status: null })),
        ),
      );
      if (cancelled) return;

      let completedAny = false;

      for (const { type, status } of results) {
        const slot = store.getState().interview.byId?.[applicationId]?.[type];

        if (status === "processing") {
          locked.add(type);
          // Don't overwrite the live stream's progress if it already owns the slot.
          if (!slot?.regenProgress) {
            dispatch(
              setProgress({
                applicationId,
                questionType: type,
                progress: { percentage: 0, message: `${formatLabel(type)} generation in progress...` },
              }),
            );
          }
        } else if (locked.has(type)) {
          // A lock we recovered has finished server-side — release it + refetch.
          locked.delete(type);
          dispatch(resetProgress({ applicationId, questionType: type }));
          completedAny = true;
        }
      }

      if (completedAny) {
        dispatch(setNeedsRefetch({ applicationId, value: true }));
        onCompleteRef.current?.();
      }

      // Keep polling only while a recovered lock is still active.
      if (!cancelled && locked.size > 0) {
        timer = setTimeout(() => poll(types), POLL_INTERVAL_MS);
      }
    };

    resolveTypes().then((types) => {
      if (!cancelled) poll(types);
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [applicationId, jobId, dispatch, store, moduleEnabled]);
};

export default useGenerationLock;
